import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSales } from "@/contexts/SalesContext";

const EVENT_LABELS = {
  paywall_hit:       { label: "Bateu no paywall", color: "bg-amber-100 text-amber-900 border-amber-300", icon: "fa-lock" },
  upgrade_whatsapp:  { label: "Clicou WhatsApp",  color: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: "fa-brands fa-whatsapp" },
  upgrade_email:     { label: "Clicou e-mail",    color: "bg-blue-100 text-blue-900 border-blue-300", icon: "fa-envelope" },
};

export default function Leads() {
  const { whatsappUrl, mailtoUrl } = useSales();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/leads")
      .then((r) => { if (mounted) setItems(r.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Group by user_email so we know the hottest leads (most touches).
  const byUser = items.reduce((acc, it) => {
    const k = it.user_email || "—";
    acc[k] = acc[k] || { email: k, name: it.user_name, company: it.company_name, events: [] };
    acc[k].events.push(it);
    return acc;
  }, {});
  const sorted = Object.values(byUser).sort((a, b) => b.events.length - a.events.length);

  if (loading) return <div className="text-slate-500"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline mb-1">Lead tracker</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Leads de upgrade</h1>
          <p className="text-sm text-slate-600 mt-1">Usuários trial que sinalizaram intenção de comprar o plano completo.</p>
        </div>
        <div className="text-right">
          <div className="kpi-number text-3xl text-emerald-900">{items.length}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">eventos · {sorted.length} pessoas</div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="card text-center py-12 text-slate-500" data-testid="leads-empty">
          <i className="fa-solid fa-temperature-empty text-3xl mb-3 text-slate-300"></i>
          <p>Ainda sem leads — quando usuários trial baterem no paywall do AEP, eles aparecem aqui.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {sorted.map((u) => (
            <div key={u.email} className="card border-slate-200" data-testid={`lead-${u.email}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-400 font-mono">{u.email}</div>
                  <div className="font-bold text-slate-900 text-lg">{u.name || "—"}</div>
                  {u.company && <div className="text-xs text-slate-600 mt-0.5">{u.company}</div>}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="risk-3 border px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
                    {u.events.length} toque(s)
                  </span>
                  <a href={whatsappUrl(`Olá ${u.name || ""}! Vi que você está testando o Sollute NR-01 e gostaria do plano completo. Posso ajudar?`)}
                     target="_blank" rel="noreferrer"
                     className="btn btn-primary text-xs" data-testid={`lead-wa-${u.email}`}>
                    <i className="fa-brands fa-whatsapp"></i> Responder
                  </a>
                  <a href={mailtoUrl(`Sollute NR-01 · plano completo`, `Olá ${u.name || ""}!\n\nObrigado pelo interesse no plano completo do Sollute NR-01...`)}
                     className="btn btn-secondary text-xs" data-testid={`lead-mail-${u.email}`}>
                    <i className="fa-solid fa-envelope"></i> E-mail
                  </a>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {u.events.map((e) => {
                  const meta = EVENT_LABELS[e.event_type] || { label: e.event_type, color: "bg-slate-100 text-slate-700 border-slate-300", icon: "fa-circle" };
                  return (
                    <span key={e.id} className={`${meta.color} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
                          title={`${new Date(e.at).toLocaleString("pt-BR")} · ${e.context || ""}`}>
                      <i className={`fa-solid ${meta.icon} mr-1`}></i>{meta.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
